/**
 * In-page switcher between Grammar and Common Sentences (URL-based links).
 *
 * @module LearningSectionSwitcher
 */

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ROUTES } from "@/lib/routeConstants";

export type LearningSection = "grammar" | "sentences";

interface LearningSectionSwitcherProps {
  readonly active: LearningSection;
}

const SECTIONS: readonly {
  id: LearningSection;
  to: string;
  labelKey: string;
}[] = [
  {
    id: "grammar",
    to: ROUTES.LEARNING_GRAMMAR,
    labelKey: "learning.sidebar.types.grammar",
  },
  {
    id: "sentences",
    to: ROUTES.LEARNING_SENTENCES,
    labelKey: "learning.sidebar.types.sentences",
  },
];

export function LearningSectionSwitcher({
  active,
}: LearningSectionSwitcherProps) {
  const { t } = useTranslation();

  return (
    <nav
      aria-label={t("learning.sectionSwitcher.label")}
      className="scrollbar-hide mb-4 overflow-x-auto"
    >
      <div className="flex w-max min-w-full rounded-lg border border-border bg-muted/40 p-1 sm:inline-flex sm:min-w-0">
        {SECTIONS.map((item) => {
          const isActive = active === item.id;
          return (
            <Link
              key={item.id}
              to={item.to}
              className={cn(
                "font-ui flex min-h-11 min-w-0 flex-1 items-center justify-center rounded-md px-3 py-2 text-center text-sm font-medium transition-colors sm:flex-none",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                isActive
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
              aria-current={isActive ? "page" : undefined}
            >
              {t(item.labelKey, {
                defaultValue:
                  item.id === "grammar" ? "Grammar" : "Common Sentences",
              })}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
